import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Button,
  ButtonGroup,
  TablePagination,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import * as XLSX from 'xlsx';
import { InventoryService } from './services/inventoryService';
import { PriceChangeLog, PriceChangeLogAggregates } from './types/inventory.types';
import { useAuth } from '../../../contexts/AuthContext';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import InventoryTableSettings from './InventoryTableSettings';
import { useTranslation } from 'react-i18next';

/** Kolon özelleştirme modalının beklediği biçim */
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  sortable?: boolean;
  description?: string;
}

const TABLE_SETTINGS_KEY = 'priceChangeLogsTableSettings';

/**
 * Excel'e aktarımda tek istekte çekilen üst sınır. Sayfadaki 25 satırı değil
 * filtrenin tamamını indirmek gerekiyor; sınırsız bırakmak yüz binlerce satırda
 * tarayıcıyı kilitler.
 */
const EXPORT_PAGE_SIZE = 50000;

const PRICE_TYPE_KEYS: { [key: string]: string } = {
  'purchasePrice': 'purchasePrice',
  'PurchasePrice': 'purchasePrice',
  'PURCHASE_PRICE': 'purchasePrice',
  'salePrice': 'salePrice',
  'SalePrice': 'salePrice',
  'SALE_PRICE': 'salePrice',
  'branchSalePrice': 'branchSalePrice',
  'BranchSalePrice': 'branchSalePrice',
  'BRANCH_SALE_PRICE': 'branchSalePrice',
  'branchPrice': 'branchPrice',
  'BranchPrice': 'branchPrice',
  'BRANCH_PRICE': 'branchPrice',
  'branchPurchasePrice': 'branchPurchasePrice',
  'BranchPurchasePrice': 'branchPurchasePrice',
  'BRANCH_PURCHASE_PRICE': 'branchPurchasePrice',
  'onlinePrice': 'onlinePrice',
  'OnlinePrice': 'onlinePrice',
  'ONLINE_PRICE': 'onlinePrice',
  'discountedPrice': 'discountedPrice',
  'DiscountedPrice': 'discountedPrice',
  'DISCOUNTED_PRICE': 'discountedPrice',
  'wholesalePrice': 'wholesalePrice',
  'WholesalePrice': 'wholesalePrice',
  'WHOLESALE_PRICE': 'wholesalePrice',
  'listPrice': 'listPrice',
  'ListPrice': 'listPrice',
  'LIST_PRICE': 'listPrice',
  'costPrice': 'costPrice',
  'CostPrice': 'costPrice',
  'COST_PRICE': 'costPrice',
};

const SOURCE_KEYS: { [key: string]: string } = {
  'INVENTORY': 'inventory',
  'Inventory': 'inventory',
  'inventory': 'inventory',
  'BRANCH_PRICE': 'branchPrice',
  'BranchPrice': 'branchPrice',
  'branchPrice': 'branchPrice',
  'BULK_UPDATE': 'bulkUpdate',
  'BulkUpdate': 'bulkUpdate',
  'bulkUpdate': 'bulkUpdate',
  'IMPORT': 'import',
  'Import': 'import',
  'import': 'import',
  'API': 'api',
  'api': 'api',
  'ECOMMERCE': 'ecommerce',
  'Ecommerce': 'ecommerce',
  'ecommerce': 'ecommerce',
  'MOBILE': 'mobile',
  'Mobile': 'mobile',
  'mobile': 'mobile',
  'SYNC': 'sync',
  'Sync': 'sync',
  'sync': 'sync',
  'MANUAL': 'manual',
  'Manual': 'manual',
  'manual': 'manual',
  'WEB': 'web',
  'Web': 'web',
  'web': 'web',
};

// Fiyat türü renkler
const PRICE_TYPE_COLORS: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' } = {
  'purchasePrice': 'info',
  'PurchasePrice': 'info',
  'PURCHASE_PRICE': 'info',
  'salePrice': 'primary',
  'SalePrice': 'primary',
  'SALE_PRICE': 'primary',
  'branchSalePrice': 'secondary',
  'BranchSalePrice': 'secondary',
  'BRANCH_SALE_PRICE': 'secondary',
  'branchPrice': 'secondary',
  'BranchPrice': 'secondary',
  'BRANCH_PRICE': 'secondary',
  'branchPurchasePrice': 'warning',
  'BranchPurchasePrice': 'warning',
  'BRANCH_PURCHASE_PRICE': 'warning',
  'onlinePrice': 'success',
  'OnlinePrice': 'success',
  'ONLINE_PRICE': 'success',
  'discountedPrice': 'error',
  'DiscountedPrice': 'error',
  'DISCOUNTED_PRICE': 'error',
  'wholesalePrice': 'primary',
  'WholesalePrice': 'primary',
  'WHOLESALE_PRICE': 'primary',
  'listPrice': 'info',
  'ListPrice': 'info',
  'LIST_PRICE': 'info',
  'costPrice': 'warning',
  'CostPrice': 'warning',
  'COST_PRICE': 'warning',
};

const PriceChangeLogs: React.FC = () => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const pickerLocale = i18n.language?.toLowerCase().startsWith('en') ? enLocale : trLocale;
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useAuth();
  
  // URL'den veya state'den companyId al
  const searchParams = new URLSearchParams(location.search);
  const companyIdFromUrl = searchParams.get('companyId');
  const itemIdFromUrl = searchParams.get('itemId');
  const itemNameFromUrl = searchParams.get('itemName');
  
  // State değişkenleri
  const [loading, setLoading] = useState<boolean>(true);
  const [logs, setLogs] = useState<PriceChangeLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [priceTypeFilter, setPriceTypeFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showInfoBox, setShowInfoBox] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [aggregates, setAggregates] = useState<PriceChangeLogAggregates | null>(null);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [isTableSettingsOpen, setIsTableSettingsOpen] = useState<boolean>(false);

  // Tablo sütunları — görünürlük kullanıcı bazında localStorage'da saklanır
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([
    { id: 'createdAt', label: t('stock.priceChangeLogs.columns.date'), field: 'createdAt', visible: true, fixed: true, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.createdAt') },
    { id: 'productName', label: t('stock.priceChangeLogs.columns.product'), field: 'productName', visible: true, fixed: true, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.productName') },
    { id: 'branchName', label: t('stock.priceChangeLogs.columns.branch'), field: 'branchName', visible: true, fixed: false, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.branchName') },
    { id: 'priceType', label: t('stock.priceChangeLogs.columns.priceType'), field: 'priceType', visible: true, fixed: false, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.priceType') },
    { id: 'oldValue', label: t('stock.priceChangeLogs.columns.oldPrice'), field: 'oldValue', visible: true, fixed: true, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.oldValue') },
    { id: 'newValue', label: t('stock.priceChangeLogs.columns.newPrice'), field: 'newValue', visible: true, fixed: true, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.newValue') },
    { id: 'change', label: t('stock.priceChangeLogs.columns.change'), field: 'change', visible: true, fixed: false, sortable: false, description: t('stock.priceChangeLogs.columnsDesc.change') },
    { id: 'currency', label: t('stock.priceChangeLogs.columns.currency'), field: 'currency', visible: false, fixed: false, sortable: false, description: t('stock.priceChangeLogs.columnsDesc.currency') },
    { id: 'userName', label: t('stock.priceChangeLogs.columns.user'), field: 'userName', visible: true, fixed: false, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.userName') },
    { id: 'source', label: t('stock.priceChangeLogs.columns.source'), field: 'source', visible: true, fixed: false, sortable: true, description: t('stock.priceChangeLogs.columnsDesc.source') },
    { id: 'notes', label: t('stock.priceChangeLogs.columns.notes'), field: 'notes', visible: false, fixed: false, sortable: false, description: t('stock.priceChangeLogs.columnsDesc.notes') },
  ]);

  // Kayıtlı kolon tercihleri — sabit kolonlar her zaman görünür kalır
  useEffect(() => {
    const saved = localStorage.getItem(TABLE_SETTINGS_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved) as TableColumn[];
      setTableColumns((defaults) =>
        defaults.map((col) => {
          const hit = parsed.find((c) => c.id === col.id);
          return hit ? { ...col, visible: col.fixed ? true : hit.visible } : col;
        }),
      );
    } catch {
      /* bozuk kayıt varsayılanları bozmasın */
    }
  }, []);

  const handleUpdateTableSettings = (columns: TableColumn[]) => {
    setTableColumns(columns);
    localStorage.setItem(TABLE_SETTINGS_KEY, JSON.stringify(columns));
  };

  // companyId: URL'den veya AuthContext'ten al
  const companyId = companyIdFromUrl || userDetails?.companyId || null;

  // companyId yoksa hata göster
  useEffect(() => {
    if (!companyId) {
      setError(t('stock.priceChangeLogs.errors.companyNotFound'));
      setLoading(false);
    } else {
      setError(null);
    }
  }, [companyId]);

  // Logları yükle - Server-side pagination ve filtreleme
  const fetchLogs = async () => {
    if (!companyId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Server-side filtreleme ve sayfalama
      const response = await InventoryService.getPriceChangeLogs(
        companyId,
        itemIdFromUrl || '', // Boş string olursa tüm loglar gelir
        page + 1,
        rowsPerPage,
        {
          searchTerm: debouncedSearchTerm,
          priceType: priceTypeFilter,
          startDate,
          endDate,
          sortColumn,
          sortDirection
        }
      );
      
      setLogs(response.data);
      setTotalCount(response.totalCount);
      setAggregates(response.aggregates ?? null);
    } catch (err: any) {
      console.error('Fiyat değişim logları yüklenirken hata:', err);
      setError(err.message || t('stock.priceChangeLogs.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Arama değişince ilk sayfaya dön
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtre değişikliklerinde sayfa sıfırla
  useEffect(() => {
    setPage(0);
  }, [priceTypeFilter, startDate, endDate, sortColumn, sortDirection]);

  // Server'dan veri çek
  useEffect(() => {
    if (companyId) {
      fetchLogs();
    }
  }, [companyId, itemIdFromUrl, page, rowsPerPage, debouncedSearchTerm, priceTypeFilter, startDate, endDate, sortColumn, sortDirection]);

  // Sayfa değiştir
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısını değiştir
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sıralama değiştir
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Fiyat değişim ikonu
  const getPriceChangeIcon = (oldValue: number, newValue: number) => {
    if (newValue > oldValue) {
      return <TrendingUpIcon color="success" fontSize="small" />;
    } else if (newValue < oldValue) {
      return <TrendingDownIcon color="error" fontSize="small" />;
    }
    return <TrendingFlatIcon color="disabled" fontSize="small" />;
  };

  // Fiyat değişim yüzdesi
  const getPriceChangePercent = (oldValue: number, newValue: number): string => {
    if (oldValue === 0) return newValue > 0 ? '+∞%' : '0%';
    const percent = ((newValue - oldValue) / oldValue) * 100;
    return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  // Para formatla
  const formatCurrency = (value: number, currency: string = 'TRY'): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Tarih formatla
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriceTypeName = (priceType: string) => {
    const key = PRICE_TYPE_KEYS[priceType];
    return key ? t(`stock.priceChangeLogs.priceTypes.${key}`) : priceType;
  };

  const getSourceName = (source?: string) => {
    if (!source) return '-';
    const key = SOURCE_KEYS[source];
    return key ? t(`stock.priceChangeLogs.sources.${key}`) : source;
  };

  /**
   * Excel'e aktar. Ekrandaki sayfa değil, filtrenin TAMAMI indirilir —
   * sayfalı bir raporda 25 satırlık dosya işe yaramıyor.
   */
  const handleExportToExcel = async () => {
    setLoadingExport(true);
    setError(null);
    try {
      const response = await InventoryService.getPriceChangeLogs(
        companyId!,
        itemIdFromUrl || '',
        1,
        EXPORT_PAGE_SIZE,
        {
          searchTerm: debouncedSearchTerm,
          priceType: priceTypeFilter,
          startDate,
          endDate,
          sortColumn,
          sortDirection
        }
      );
      writeExcel(response.data);
    } catch (err: any) {
      console.error('Excel aktarımı başarısız:', err);
      setError(err.message || t('stock.priceChangeLogs.errors.loadFailed'));
    } finally {
      setLoadingExport(false);
    }
  };

  const writeExcel = (rows: PriceChangeLog[]) => {
    const exportData = rows.map(log => ({
      [t('stock.priceChangeLogs.columns.date')]: formatDate(log.createdAt),
      [t('stock.priceChangeLogs.columns.productName')]: log.productName || '',
      [t('stock.priceChangeLogs.columns.stockCode')]: log.stockCode || '',
      [t('stock.priceChangeLogs.columns.branch')]: log.branchName || t('stock.priceChangeLogs.general'),
      [t('stock.priceChangeLogs.columns.priceType')]: getPriceTypeName(log.priceType),
      [t('stock.priceChangeLogs.columns.oldPrice')]: log.oldValue || 0,
      [t('stock.priceChangeLogs.columns.newPrice')]: log.newValue || 0,
      [t('stock.priceChangeLogs.columns.changePercent')]: getPriceChangePercent(log.oldValue || 0, log.newValue || 0),
      [t('stock.priceChangeLogs.columns.currency')]: log.currency || 'TRY',
      [t('stock.priceChangeLogs.columns.user')]: log.userName || t('stock.priceChangeLogs.unknownUser'),
      [t('stock.priceChangeLogs.columns.source')]: getSourceName(log.source),
      [t('stock.priceChangeLogs.columns.notes')]: log.notes || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('stock.priceChangeLogs.title'));
    
    const fileName = `price_change_logs_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Benzersiz fiyat türleri - PRICE_TYPE_NAMES'den al
  const uniquePriceTypes = Object.keys(PRICE_TYPE_KEYS).filter((key, index, self) => {
    // Sadece bir varyasyonu al (örn: SALE_PRICE, SalePrice, salePrice -> sadece biri)
    const normalizedKey = key.toUpperCase().replace(/_/g, '');
    const firstIndex = self.findIndex(k => k.toUpperCase().replace(/_/g, '') === normalizedKey);
    return firstIndex === index;
  });

  // ─── Kolon Tanımları ───────────────────────────────
  // Tümü tanımlanır, görünürlük filtresi aşağıda uygulanır
  const allColumns = useMemo<DataTableColumn<PriceChangeLog>[]>(() => [
    {
      id: 'createdAt',
      label: t('stock.priceChangeLogs.columns.date'),
      field: 'createdAt' as keyof PriceChangeLog,
      sortable: true,
      width: '140px',
      render: (log) => (
        <Typography variant="body2">{formatDate(log.createdAt)}</Typography>
      ),
    },
    {
      id: 'productName',
      label: t('stock.priceChangeLogs.columns.product'),
      field: 'productName' as keyof PriceChangeLog,
      sortable: true,
      render: (log) => (
        <>
          <Typography variant="body2" fontWeight="medium">{log.productName}</Typography>
          <Typography variant="caption" color="text.secondary">{log.stockCode}</Typography>
        </>
      ),
    },
    {
      id: 'branchName',
      label: t('stock.priceChangeLogs.columns.branch'),
      render: (log) => (
        <Typography variant="body2">{log.branchName || t('stock.priceChangeLogs.general')}</Typography>
      ),
    },
    {
      id: 'priceType',
      label: t('stock.priceChangeLogs.columns.priceType'),
      field: 'priceType' as keyof PriceChangeLog,
      sortable: true,
      render: (log) => (
        <Chip
          label={getPriceTypeName(log.priceType)}
          size="small"
          color={PRICE_TYPE_COLORS[log.priceType] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'oldValue',
      label: t('stock.priceChangeLogs.columns.oldPrice'),
      field: 'oldValue' as keyof PriceChangeLog,
      sortable: true,
      align: 'right',
      render: (log) => (
        <Typography variant="body2">{formatCurrency(log.oldValue || 0, log.currency)}</Typography>
      ),
    },
    {
      id: 'newValue',
      label: t('stock.priceChangeLogs.columns.newPrice'),
      field: 'newValue' as keyof PriceChangeLog,
      sortable: true,
      align: 'right',
      render: (log) => {
        const isIncrease = (log.newValue || 0) > (log.oldValue || 0);
        const isDecrease = (log.newValue || 0) < (log.oldValue || 0);
        return (
          <Typography
            variant="body2"
            fontWeight="medium"
            color={isIncrease ? 'success.main' : isDecrease ? 'error.main' : 'inherit'}
          >
            {formatCurrency(log.newValue || 0, log.currency)}
          </Typography>
        );
      },
    },
    {
      id: 'change',
      label: t('stock.priceChangeLogs.columns.change'),
      align: 'center',
      render: (log) => {
        const oldVal = log.oldValue || 0;
        const newVal = log.newValue || 0;
        const isIncrease = newVal > oldVal;
        const isDecrease = newVal < oldVal;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            {getPriceChangeIcon(oldVal, newVal)}
            <Typography
              variant="caption"
              fontWeight="medium"
              color={isIncrease ? 'success.main' : isDecrease ? 'error.main' : 'text.secondary'}
            >
              {getPriceChangePercent(oldVal, newVal)}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'userName',
      label: t('stock.priceChangeLogs.columns.user'),
      field: 'userName' as keyof PriceChangeLog,
      sortable: true,
      render: (log) => (
        <Typography variant="body2">{log.userName || t('stock.priceChangeLogs.unknownUser')}</Typography>
      ),
    },
    {
      id: 'source',
      label: t('stock.priceChangeLogs.columns.source'),
      render: (log) => (
        <Typography variant="caption" color="text.secondary">
          {getSourceName(log.source)}
        </Typography>
      ),
    },
    {
      id: 'currency',
      label: t('stock.priceChangeLogs.columns.currency'),
      align: 'center',
      render: (log) => <Typography variant="body2">{log.currency || 'TRY'}</Typography>,
    },
    {
      id: 'notes',
      label: t('stock.priceChangeLogs.columns.notes'),
      render: (log) => (
        <Typography variant="caption" color="text.secondary">
          {log.notes || '-'}
        </Typography>
      ),
    },
  ], [t, locale]);

  /**
   * Görünür kolonlar, kullanıcının modalda belirlediği SIRAYLA.
   * Sıra tableColumns'tan gelir; allColumns yalnızca çizim tanımını taşır.
   */
  const priceLogColumns = useMemo<DataTableColumn<PriceChangeLog>[]>(() => {
    const byId = new Map(allColumns.map((c) => [c.id, c]));
    return tableColumns
      .filter((c) => c.visible)
      .map((c) => byId.get(c.id))
      .filter((c): c is DataTableColumn<PriceChangeLog> => !!c);
  }, [allColumns, tableColumns]);

  // ─── Koşullu Satır Renklendirme ───────────────────
  const getLogRowSx = useCallback((log: PriceChangeLog) => {
    const oldVal = log.oldValue || 0;
    const newVal = log.newValue || 0;
    const isIncrease = newVal > oldVal;
    const isDecrease = newVal < oldVal;
    return {
      backgroundColor: isIncrease
        ? 'rgba(76, 175, 80, 0.05)'
        : isDecrease
          ? 'rgba(244, 67, 54, 0.05)'
          : 'inherit'
    };
  }, []);

  const hasActiveFilter = !!searchTerm || priceTypeFilter !== 'all' || !!startDate || !!endDate;

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setPriceTypeFilter('all');
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <Box component="main" sx={{ p: 3 }}>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/stok')}
            sx={{ mr: 2 }}
          >
            {t('stock.priceChangeLogs.actions.back')}
          </Button>
          <HistoryIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
          <Box>
            <Typography variant="h5" component="h1" fontWeight="bold">
              {t('stock.priceChangeLogs.title')}
            </Typography>
            {itemNameFromUrl && (
              <Typography variant="body2" color="text.secondary">
                {itemNameFromUrl}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={11} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('stock.priceChangeLogs.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Filtreleri Göster */}
          <Grid item xs={1} md={0.5} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title={showFilters ? t('stock.actions.hideFilters') : t('stock.actions.showFilters')}>
              <IconButton
                color={showFilters ? 'primary' : 'default'}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                {showFilters ? <ExpandLessIcon /> : <FilterListIcon />}
              </IconButton>
            </Tooltip>
          </Grid>

          {/* Sağ taraf - Butonlar */}
          <Grid
            item
            xs={12}
            md={7.5}
            sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, gap: 1, flexWrap: 'wrap' }}
          >
            <Tooltip title={t('stock.priceChangeLogs.actions.info')}>
              <IconButton
                size="small"
                color={showInfoBox ? 'primary' : 'default'}
                onClick={() => setShowInfoBox(prev => !prev)}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('stock.priceChangeLogs.actions.refresh')}>
              <span>
                <IconButton size="small" onClick={fetchLogs} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <ButtonGroup size="small">
              <Button
                variant="outlined"
                startIcon={<AssessmentIcon />}
                onClick={handleExportToExcel}
                disabled={loadingExport || loading || totalCount === 0}
              >
                {loadingExport
                  ? t('stock.report.actions.downloading')
                  : t('stock.priceChangeLogs.actions.downloadExcel')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={() => setIsTableSettingsOpen(true)}
              >
                {t('stock.menu.customizeTable')}
              </Button>
            </ButtonGroup>

            {hasActiveFilter && (
              <Button size="small" variant="outlined" color="secondary" onClick={handleClearAllFilters}>
                {t('stock.priceChangeLogs.actions.clearFilters')}
              </Button>
            )}
          </Grid>
        </Grid>

        {showInfoBox && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              {t('stock.priceChangeLogs.infoBox.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('stock.priceChangeLogs.infoBox.description')}
            </Typography>
            <Typography variant="body2">{t('stock.priceChangeLogs.infoBox.item1')}</Typography>
            <Typography variant="body2">{t('stock.priceChangeLogs.infoBox.item2')}</Typography>
            <Typography variant="body2">{t('stock.priceChangeLogs.infoBox.item3')}</Typography>
          </Alert>
        )}

        {/* Gizlenebilir Filtreler */}
        {showFilters && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('stock.priceChangeLogs.filters.priceType')}</InputLabel>
                  <Select
                    value={priceTypeFilter}
                    label={t('stock.priceChangeLogs.filters.priceType')}
                    onChange={(e) => setPriceTypeFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('stock.priceChangeLogs.filters.all')}</MenuItem>
                    {uniquePriceTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {getPriceTypeName(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pickerLocale}>
                  <DateTimePicker
                    label={t('stock.priceChangeLogs.filters.startDate')}
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pickerLocale}>
                  <DateTimePicker
                    label={t('stock.priceChangeLogs.filters.endDate')}
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ mb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
              borderRadius: 2
            }}
          >
            <HistoryIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
              {t('stock.priceChangeLogs.emptyState.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {hasActiveFilter
                ? t('stock.priceChangeLogs.emptyState.notFound')
                : t('stock.priceChangeLogs.emptyState.noRecords')}
            </Typography>
          </Box>
        ) : (
          <>
            <AccountingDataTable<PriceChangeLog>
              columns={priceLogColumns}
              rows={logs}
              getRowId={(log) => log.id}
              loading={false}
              pagination={false}
              onSort={(field, direction) => {
                setSortColumn(field);
                setSortDirection(direction);
              }}
              sortField={sortColumn}
              sortDirection={sortDirection}
              getRowSx={getLogRowSx}
              emptyMessage={t('stock.priceChangeLogs.empty')}
              ariaLabel={t('stock.priceChangeLogs.tableAriaLabel')}
              columnResizeStorageKey="price-change-logs"
            />

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t('stock.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('stock.pagination.displayedRows', { from, to, count })
              }
            />

            {/* Toplam Satırı — filtrenin tamamını özetler, sayfayı değil */}
            {totalCount > 0 && (
              <Paper elevation={1} sx={{ mt: 1, p: 1.5, borderTop: '2px solid', borderTopColor: 'primary.main' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" color="primary.dark">
                    {t('stock.priceChangeLogs.summary.totalRecords', { count: totalCount })}
                  </Typography>
                  {aggregates && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TrendingUpIcon color="success" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {t('stock.priceChangeLogs.summary.increases')}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {aggregates.increaseCount}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TrendingDownIcon color="error" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {t('stock.priceChangeLogs.summary.decreases')}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          {aggregates.decreaseCount}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('stock.priceChangeLogs.summary.averageChange')}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={
                            aggregates.averageChangePercent > 0
                              ? 'success.main'
                              : aggregates.averageChangePercent < 0
                                ? 'error.main'
                                : 'text.primary'
                          }
                        >
                          {aggregates.averageChangePercent > 0 ? '+' : ''}
                          {aggregates.averageChangePercent.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </>
        )}

        {/* Sayfa altına ek boşluk */}
        <Box sx={{ height: '200px' }} />
      </Box>

      {/* Tablo Ayarları Modalı */}
      <InventoryTableSettings
        open={isTableSettingsOpen}
        onClose={() => setIsTableSettingsOpen(false)}
        onSave={handleUpdateTableSettings}
        currentColumns={tableColumns}
      />
    </Box>
  );
};

export default PriceChangeLogs;
